import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, Clock, ArrowLeft, Share2 } from 'lucide-react';
import SEOHelmet from "@/components/SEOHelmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { trackServiceView } from "@/components/GoogleAnalytics";

// This would typically come from a CMS or API
const blogPosts = {
  'organizing-small-spaces': {
    title: '5 Genius Ways to Organize Small Spaces in Michigan Homes',
    excerpt: 'Maximize every square foot with these professional organizing tips specifically for Michigan\'s unique home layouts.',
    content: `Living in Michigan often means dealing with older homes that have smaller spaces, limited storage, and unique architectural challenges. Whether you're in a historic Detroit bungalow or a cozy Grand Rapids apartment, these professional organizing strategies will help you make the most of every inch.

## 1. Vertical Storage Solutions

Michigan homes often have high ceilings but limited floor space. Take advantage of vertical space with:
- Tall, narrow shelving units that don't overwhelm small rooms
- Over-door organizers for pantries and closets
- Wall-mounted storage in bathrooms and kitchens
- Ceiling-height bookcases with adjustable shelves

## 2. Seasonal Rotation Systems

Michigan's four distinct seasons mean you need clothes and gear for every weather condition. Create a rotation system:
- Store off-season clothes in under-bed boxes
- Use vacuum-sealed bags for bulky winter items
- Designate a "seasonal swap" area in your basement or attic
- Keep current season items easily accessible

## 3. Multi-Functional Furniture

Every piece should serve multiple purposes:
- Ottoman storage benches for entryways
- Bed frames with built-in drawers
- Coffee tables with hidden compartments
- Dining tables with expandable leaves for entertaining

## 4. Kitchen Efficiency

Michigan kitchens often lack pantry space. Maximize what you have:
- Use drawer dividers for utensils and small items
- Install pull-out shelves in lower cabinets
- Add magnetic strips for knives and spice jars
- Use clear containers for bulk items to save space

## 5. Basement Organization

Most Michigan homes have basements that become catch-all spaces. Transform yours:
- Install moisture-resistant shelving systems
- Use clear, sealed containers for storage
- Create zones: holiday decorations, sports equipment, tools
- Add proper lighting to make the space functional

Ready to transform your small space? Our Michigan-based team specializes in maximizing storage in homes just like yours.`,
    category: 'Home Organization',
    author: 'Organize by Design Team',
    date: '2024-01-15',
    readTime: '5 min read',
    tags: ['Small Spaces', 'Michigan Homes', 'Storage Solutions'],
    image: '/lovable-uploads/9c140064-27be-41e6-88d6-c34a304242e4.png'
  },
  'seasonal-organization': {
    title: 'Michigan Seasonal Organization: From Snow Boots to Swimsuits',
    excerpt: 'Master the art of seasonal transitions with storage solutions designed for Michigan\'s extreme weather changes.',
    content: `Michigan's dramatic seasonal changes require a strategic approach to home organization. From heavy winter coats to summer sports equipment, here's how to manage it all efficiently.

## Winter Preparation

Michigan winters are serious business. Your organization system should reflect that:

### Entryway Setup
- Boot trays with drainage systems
- Hooks at multiple heights for family members
- Basket storage for hats, gloves, and scarves
- Umbrella stands for both snow and rain gear

### Coat Storage
- Invest in a sturdy coat closet system
- Use slim velvet hangers to maximize space
- Add shelf dividers for winter accessories
- Consider a cedar chest for wool items

## Spring Cleaning & Transition

The Michigan spring thaw is the perfect time for deep organization:

### Clothing Rotation
- Store winter clothes in vacuum-sealed bags
- Check for damage from winter storage
- Donate items that didn't get worn
- Prepare lighter jackets for unpredictable spring weather

### Garage & Basement Organization
- Clean and reorganize after winter storage period
- Check for moisture damage from snow melts
- Prepare garden tools and outdoor equipment
- Service and store snow removal equipment

## Summer Organization

Michigan summers are short but sweet. Make the most of them:

### Outdoor Equipment
- Create designated zones for sports equipment
- Use wall-mounted bike storage
- Organize camping and lake gear together
- Set up easy-access summer toy storage

### Vacation Preparation
- Create packing lists for Up North trips
- Designate beach/lake bag storage
- Organize summer clothes by activity type

## Fall Preparation

Get ready for Michigan's beautiful but brief fall:

### Back-to-School Organization
- Set up homework stations
- Organize school supplies and backpacks
- Create morning routine stations
- Prepare heavier clothing storage

### Holiday Storage Setup
- Plan decoration storage before you need it
- Create gift-wrapping stations
- Organize baking supplies for holiday cooking

Ready for a seamless seasonal transition? Our Michigan team knows exactly what storage challenges you face.`,
    category: 'Seasonal Organization',
    author: 'Organize by Design Team',
    date: '2024-01-10',
    readTime: '7 min read',
    tags: ['Seasonal', 'Michigan Weather', 'Clothes Storage'],
    image: '/lovable-uploads/c325d0e4-f21b-4854-acf2-360ae2b9ab61.png'
  },
  'adhd-friendly-systems': {
    title: 'ADHD-Friendly Organization Systems That Actually Work',
    excerpt: 'Simple, visual, and sustainable organizing strategies designed specifically for neurodivergent brains.',
    content: `Traditional organization systems often fail people with ADHD because they're designed for neurotypical brains. Here are research-backed strategies that work with ADHD, not against it.

## Visual Organization is Key

ADHD brains are highly visual. Use this to your advantage:

### Clear Containers Everywhere
- Glass jars for pantry items
- Clear plastic bins for closet storage
- Transparent shoe boxes for accessories
- See-through drawer organizers

### Color-Coding Systems
- Assign colors to family members or categories
- Use colored bins, labels, and folders
- Create visual maps of your systems
- Make it obvious and intuitive

## The "One Touch" Rule

ADHD brains struggle with multi-step processes. Simplify:

### Single-Step Solutions
- Hooks instead of hangers when possible
- Open bins instead of lidded containers
- Designated "homes" for frequently used items
- Eliminate unnecessary steps in your routines

## Body Doubling & Accountability

Organization is easier with support:

### Professional Guidance
- Regular check-ins with an organizer
- Body doubling sessions for overwhelming tasks
- Systematic approach to prevent overwhelm
- Celebration of small wins

## Maintenance Systems

ADHD brains need different maintenance approaches:

### The "Good Enough" Standard
- Progress over perfection
- 80% organized is better than 0%
- Quick reset routines (10-15 minutes)
- Forgiving systems that bounce back

### Daily Minimums
- One small organizing task per day
- Visual reminders and cues
- Reward systems for consistency
- Flexible timing that works with your energy

## Common ADHD Organizing Mistakes to Avoid

1. **Over-complicated systems** - Keep it simple
2. **Too many categories** - Broader groupings work better
3. **Hidden storage** - Out of sight = out of mind
4. **All-or-nothing thinking** - Small progress counts

Our ADHD-friendly organizing approach celebrates neurodivergent minds while creating systems that actually stick.`,
    category: 'ADHD Systems',
    author: 'Organize by Design Team',
    date: '2024-01-05',
    readTime: '6 min read',
    tags: ['ADHD', 'Neurodivergent', 'Systems', 'Visual Organization'],
    image: '/lovable-uploads/ad558071-0db2-4301-9fb5-c1235ec6c9b2.png'
  },
  'real-estate-staging': {
    title: 'Home Staging Secrets: Sell Your Michigan Home Faster',
    excerpt: 'Professional staging tips that help Michigan homes sell 30% faster and for higher prices.',
    content: `In Michigan's competitive real estate market, proper staging can make or break a sale. Here's what we've learned from helping hundreds of Michigan homeowners prepare their properties.

## First Impressions Matter Most

Michigan buyers often see multiple homes in one day. Make yours memorable:

### Curb Appeal for Michigan Seasons
- Address seasonal yard maintenance
- Fresh paint on front doors (popular colors: navy, forest green, burgundy)
- Power wash driveways and sidewalks
- Add seasonal planters (choose hardy Michigan plants)

### Entryway Impact
- Remove all personal items from entryways
- Add a simple bench or console table
- Ensure excellent lighting
- Keep floors spotless (especially important in winter)

## Room-by-Room Staging Strategy

### Living Areas
- Remove 50% of furniture to show space
- Create conversation areas with furniture
- Add mirrors to reflect natural light
- Use neutral colors with small pops of color

### Kitchens
- Clear all countertops except 2-3 carefully chosen items
- Remove magnets and papers from refrigerator
- Add fresh flowers or a bowl of lemons
- Ensure all appliances are spotless

### Bedrooms
- Use hotel-style bedding in neutral colors
- Remove personal photos and clothing
- Add bedside lamps for warm lighting
- Keep closets organized and spacious-looking

### Bathrooms
- Remove all personal toiletries
- Add fresh towels in neutral colors
- Include a small plant or candle for ambiance
- Ensure excellent lighting and ventilation

## Michigan-Specific Staging Tips

### Basement Staging
- Address any moisture issues immediately
- Add proper lighting throughout
- Show potential uses: office, gym, family room
- Remove storage to show available space

### Seasonal Considerations
- Winter showings: ensure walkways are clear and salted
- Summer showings: highlight outdoor spaces and lake access
- Fall showings: emphasize cozy indoor spaces
- Spring showings: showcase natural light and outdoor potential

## What Not to Do

Common staging mistakes we see in Michigan:
- Leaving personal photos and memorabilia
- Over-decorating with trendy items
- Ignoring pet odors
- Cluttered garage and basement spaces
- Poor lighting in already-dark winter months

## The ROI of Professional Staging

Staged homes in Michigan typically:
- Sell 87% faster than non-staged homes
- Receive offers 30% sooner
- Sell for 6-10% more than asking price
- Spend 23 fewer days on market

Ready to maximize your home's selling potential? Our staging experts know exactly what Michigan buyers want to see.`,
    category: 'Real Estate',
    author: 'Organize by Design Team',
    date: '2023-12-28',
    readTime: '8 min read',
    tags: ['Home Staging', 'Real Estate', 'Michigan Market'],
    image: '/lovable-uploads/85e7cc8e-da9b-4008-b5b9-298ebebad704.png'
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = slug ? blogPosts[slug as keyof typeof blogPosts] : null;

  const handleBookingClick = () => {
    trackServiceView(`Blog Post - ${post?.title}`);
    navigate('/book');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post?.title,
          text: post?.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Share failed', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif font-bold mb-4">Post Not Found</h1>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHelmet 
        title={`${post.title} | Organize by Design Michigan`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        canonicalUrl={`https://organizebydesignmichigan.com/blog/${slug}`}
      />
      
      <Header onBookingClick={handleBookingClick} />
      
      <main className="min-h-screen bg-background">
        {/* Hero Image */}
        <div className="relative h-[50vh] overflow-hidden">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="container mx-auto px-4 text-center text-white">
              <Badge variant="secondary" className="mb-4 text-sm">
                {post.category}
              </Badge>
              <h1 className="text-3xl md:text-5xl font-serif font-bold mb-4 max-w-4xl mx-auto">
                {post.title}
              </h1>
              <div className="flex items-center justify-center space-x-6 text-sm">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  {post.author}
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(post.date).toLocaleDateString()}
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {post.readTime}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Navigation and Share */}
              <div className="flex items-center justify-between mb-8">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/blog')}
                  className="flex items-center"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Blog
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleShare}
                  className="flex items-center"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>
              </div>

              {/* Article Body */}
              <div className="prose prose-lg max-w-none">
                {post.content.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('## ')) {
                    return (
                      <h2 key={index} className="text-2xl font-serif font-semibold mt-8 mb-4 text-foreground">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  } else if (paragraph.startsWith('### ')) {
                    return (
                      <h3 key={index} className="text-xl font-serif font-semibold mt-6 mb-3 text-foreground">
                        {paragraph.replace('### ', '')}
                      </h3>
                    );
                  } else if (paragraph.startsWith('- ')) {
                    return (
                      <li key={index} className="ml-6 mb-2 text-muted-foreground">
                        {paragraph.replace('- ', '')}
                      </li>
                    );
                  } else if (paragraph.trim() === '') {
                    return <br key={index} />;
                  } else {
                    return (
                      <p key={index} className="mb-4 text-muted-foreground leading-relaxed">
                        {paragraph}
                      </p>
                    );
                  }
                })}
              </div>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-12 p-8 bg-secondary/10 rounded-lg text-center">
                <h3 className="text-2xl font-serif font-bold mb-4">
                  Ready to Get Organized?
                </h3>
                <p className="text-muted-foreground mb-6">
                  Get personalized help from our Michigan organizing experts.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={handleBookingClick}
                    size="lg"
                    className="brand-gradient hover:opacity-90 text-white font-semibold"
                  >
                    Book Free Consultation
                  </Button>
                  <Button 
                    onClick={() => navigate('/resources')}
                    variant="outline" 
                    size="lg"
                  >
                    Download Free Guides
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer onBookingClick={handleBookingClick} />
    </>
  );
};

export default BlogPost;